import React, { useCallback, useEffect, useRef, useState } from "react";
import PostalMime from "postal-mime";
import { ReactComponent as NoteIcon } from "../../../images/note-2_blue.svg";
import { ReactComponent as ImageIcon } from "../../../images/file_blue.svg";
import Loading from "../../../ui-components/loading/loading";
import EmailHeaderPanel, { EmailHeaderPanelProps } from "./EmailHeaderPanel";
import EmailAttachmentPreview from "./EmailAttachmentPreview";
import {
  MimeAttachmentContent,
  canPreviewAttachment,
  downloadAttachmentBlob,
} from "./emailAttachmentUtils";
import "./emailViewer.scss";

export interface EmailMimeViewerProps extends EmailHeaderPanelProps {
  rawMimeBase64: string | null;
  isLoading?: boolean;
  isError?: boolean;
  showHeader?: boolean;
}

const IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"];

const decodeBase64ToUint8Array = (base64: string): Uint8Array => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const EmailMimeViewer: React.FC<EmailMimeViewerProps> = ({
  rawMimeBase64,
  isLoading,
  isError,
  showHeader = true,
  viewEmailAsLink = false,
  onViewEmail,
  ...headerProps
}) => {
  const [parsedHtml, setParsedHtml] = useState<string | null>(null);
  const [parsedText, setParsedText] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<MimeAttachmentContent[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [previewHeight, setPreviewHeight] = useState(200);
  const [previewAttachmentIndex, setPreviewAttachmentIndex] = useState<number | null>(null);
  const previewRef = useRef<HTMLIFrameElement | null>(null);

  const parseEmail = useCallback(async () => {
    if (!rawMimeBase64) {
      setParsedHtml(null);
      setParsedText(null);
      setAttachments([]);
      setPreviewAttachmentIndex(null);
      return;
    }

    setIsParsing(true);
    try {
      const bytes = decodeBase64ToUint8Array(rawMimeBase64);
      const result = await PostalMime.parse(bytes, { attachmentEncoding: "base64" });
      const inlineImages = result.attachments.filter((a) => a.disposition === "inline");
      const fileAttachments = result.attachments.filter(
        (a) => a.disposition === "attachment" || (!a.disposition && a.filename)
      );
      const { html, text } = result;

      if (html) {
        const withCid = inlineImages.reduce(
          (acc, image) =>
            image.contentId
              ? acc.replace(
                  `cid:${image.contentId.replace(/^<|>$/g, "")}`,
                  `data:${image.mimeType || "image/jpeg"};base64,${image.content}`
                )
              : acc,
          html
        );
        setParsedHtml(withCid);
        setParsedText(null);
      } else {
        setParsedHtml(null);
        setParsedText(text || null);
      }

      setAttachments(
        fileAttachments.map((a) => ({
          filename: a.filename || "attachment",
          mimeType: a.mimeType || "application/octet-stream",
          content: a.content,
        }))
      );
      setPreviewAttachmentIndex(null);
    } catch {
      setParsedHtml(null);
      setParsedText(null);
      setAttachments([]);
      setPreviewAttachmentIndex(null);
    } finally {
      setIsParsing(false);
    }
  }, [rawMimeBase64]);

  useEffect(() => {
    parseEmail();
  }, [parseEmail]);

  useEffect(() => {
    if (
      previewAttachmentIndex != null &&
      (previewAttachmentIndex < 0 || previewAttachmentIndex >= attachments.length)
    ) {
      setPreviewAttachmentIndex(null);
    }
  }, [attachments.length, previewAttachmentIndex]);

  const showLoading = isLoading || isParsing;
  const previewAttachment =
    previewAttachmentIndex != null ? attachments[previewAttachmentIndex] : null;

  return (
    <div className="xgs-email-viewer">
      {showHeader && (
        <EmailHeaderPanel
          {...headerProps}
          viewEmailAsLink={viewEmailAsLink}
          onViewEmail={onViewEmail}
        />
      )}

      <div className="xgs-email-viewer__body-section">
        <div className="xgs-email-viewer__section-title">Email content</div>
        <div className="xgs-email-viewer__content">
          {showLoading ? (
            <Loading isLoading />
          ) : isError ? (
            <div className="xgs-email-viewer__error">No email content available.</div>
          ) : (
            <>
              {attachments.length > 0 && (
                <div className="xgs-email-viewer__attachments">
                  <div className="xgs-email-viewer__attachments-title">Attachments</div>
                  <div className="xgs-email-viewer__attachments-list">
                    {attachments.map((attachment, index) => {
                      const isPreviewing = previewAttachmentIndex === index;
                      const canPreview = canPreviewAttachment(
                        attachment.mimeType,
                        attachment.filename
                      );
                      return (
                        <div
                          key={`${attachment.filename}-${index}`}
                          className={`xgs-email-viewer__attachment${
                            isPreviewing ? " xgs-email-viewer__attachment--active" : ""
                          }`}
                        >
                          <div className="xgs-email-viewer__attachment-icon">
                            {IMAGE_TYPES.includes(attachment.mimeType) ? (
                              <ImageIcon />
                            ) : (
                              <NoteIcon />
                            )}
                          </div>
                          <div className="xgs-email-viewer__attachment-info">
                            <span className="xgs-email-viewer__attachment-name">
                              {attachment.filename}
                            </span>
                            <span className="xgs-email-viewer__attachment-ext">
                              {(attachment.filename.split(".").pop() || "FILE").toUpperCase()}
                            </span>
                          </div>
                          <div className="xgs-email-viewer__attachment-actions">
                            {canPreview && (
                              <button
                                type="button"
                                className="xgs-email-viewer__attachment-action"
                                onClick={() =>
                                  setPreviewAttachmentIndex(isPreviewing ? null : index)
                                }
                              >
                                {isPreviewing ? "Hide" : "View"}
                              </button>
                            )}
                            <button
                              type="button"
                              className="xgs-email-viewer__attachment-action"
                              onClick={() => downloadAttachmentBlob(attachment)}
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {previewAttachment && (
                <EmailAttachmentPreview
                  attachment={previewAttachment}
                  onClose={() => setPreviewAttachmentIndex(null)}
                />
              )}

              {parsedHtml && (
                <div className="xgs-email-viewer__iframe-wrap">
                  <iframe
                    ref={previewRef}
                    title="email-preview"
                    style={{ borderWidth: 0 }}
                    srcDoc={parsedHtml}
                    width="100%"
                    height={previewHeight}
                    onLoad={() => {
                      setPreviewHeight(
                        previewRef.current?.contentWindow?.document.documentElement.scrollHeight ||
                          200
                      );
                    }}
                  />
                </div>
              )}
              {!parsedHtml && parsedText && (
                <pre className="xgs-email-viewer__text">{parsedText}</pre>
              )}
              {!parsedHtml && !parsedText && !attachments.length && !showLoading && (
                <div className="xgs-email-viewer__error">No email body to display.</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailMimeViewer;
